import { arenaDetailsSchema, type ArenaDetails, type OpeningSlot } from '@growup/arena-shared';
import { Button, Card, Checkbox, Field, Input, Notice, StatusBadge, Textarea } from '@growup/ui';
import { useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { overlapErrors, sortSlots } from './openingHours';
import { WeeklyHours } from './WeeklyHours';

type TextKey = Exclude<keyof ArenaDetails, 'openingHours' | 'published'>;

const TEXT_FIELDS: { key: TextKey; label: string; hint?: string }[] = [
  { key: 'address', label: 'Morada' },
  { key: 'postalCode', label: 'Código postal' },
  { key: 'city', label: 'Localidade' },
  {
    key: 'mapUrl',
    label: 'Link do mapa',
    hint: 'Google Maps ou OpenStreetMap, começa por https://',
  },
  { key: 'phone', label: 'Telefone' },
  { key: 'email', label: 'Email' },
  { key: 'hoursNote', label: 'Nota ao horário', hint: 'Por exemplo: fechado em feriados.' },
];

type Form = Record<TextKey, string> & { openingHours: OpeningSlot[]; published: boolean };

function toForm(details: ArenaDetails): Form {
  return {
    description: details.description ?? '',
    address: details.address ?? '',
    postalCode: details.postalCode ?? '',
    city: details.city ?? '',
    mapUrl: details.mapUrl ?? '',
    phone: details.phone ?? '',
    email: details.email ?? '',
    hoursNote: details.hoursNote ?? '',
    openingHours: details.openingHours,
    published: details.published,
  };
}

/** What growup.gg/arena shows about this arena. Administrators only. */
export function PublicInfoCard({ arenaId }: { arenaId: string }) {
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getJson(`/api/arena/arenas/${arenaId}/details`, arenaDetailsSchema)
      .then((d) => setForm(toForm(d)))
      .catch((e: unknown) => setError(messageOf(e)));
  }, [arenaId]);

  if (!form) {
    return (
      <Card headingLevel={2} title="No site">
        {error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>}
      </Card>
    );
  }

  const set = (patch: Partial<Form>) => {
    setSaved(false);
    setForm({ ...form, ...patch });
  };
  const save = async () => {
    setError(null);
    const parsed = arenaDetailsSchema.safeParse({
      ...form,
      openingHours: sortSlots(form.openingHours),
    });
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => i.message).join(' '));
      return;
    }
    const overlaps = overlapErrors(parsed.data.openingHours);
    if (overlaps.length > 0) {
      setError(overlaps.join(' '));
      return;
    }
    try {
      await send('PUT', `/api/arena/arenas/${arenaId}/details`, parsed.data);
      setForm(toForm(parsed.data));
      setSaved(true);
    } catch (e) {
      setError(messageOf(e));
    }
  };

  return (
    <Card headingLevel={2} title="No site">
      <p className="mb-3 text-body text-gu-ink-muted">
        O que a página growup.gg/arena mostra desta arena. Só aparece quando está publicada.
      </p>
      <Field label="Descrição" optional>
        <Textarea
          rows={3}
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </Field>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {TEXT_FIELDS.map((f) => (
          <Field key={f.key} label={f.label} hint={f.hint} optional>
            <Input value={form[f.key]} onChange={(e) => set({ [f.key]: e.target.value })} />
          </Field>
        ))}
      </div>

      <fieldset className="mt-4">
        <legend className="mb-2 text-body font-semibold">Horário</legend>
        {form.openingHours.length === 0 ? (
          <p className="mb-2 text-body text-gu-ink-muted">
            Sem horário: o site diz «horário a anunciar».
          </p>
        ) : null}
        <WeeklyHours value={form.openingHours} onChange={(openingHours) => set({ openingHours })} />
      </fieldset>

      <div className="mt-4">
        <Checkbox
          label="Publicar no site"
          checked={form.published}
          onChange={(e) => set({ published: e.target.checked })}
        />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button onClick={() => void save()}>Guardar</Button>
        {saved ? <StatusBadge tone="success">Guardado</StatusBadge> : null}
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </Card>
  );
}
